/**
 * Rules Catalog Invariants (Reglas tab)
 *
 * Enforces:
 * 1. Permissions — source-based command access control (permission matrix).
 * 2. Normalization — light state consistency only.
 * 3. Validation — none (no form in this view).
 *
 * Plus exported `selectors` (matching the activity/itemList convention) that
 * derive the inheritance-resolved rules view the components consume. The heavy
 * compute (the precedence/override/bypass/disabled walk) lives in the selectors
 * via `resolveRulesForScope` so `normalize` stays light.
 *
 * CORE GUARD (SDD "The Inheritance Resolver"): the Reglas table must NEVER show
 * a rule as active under a scope where the backend's `findRulesToExecute` walk
 * would not. The "activas" count and the `active` filter therefore EXCLUDE rules
 * annotated `overridden` or `bypassed` — they are suppressed, not active.
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import {
  resolveRulesForScope,
  ResolvedRule
} from '../../shared/utils/resolveRulesForScope';
import { ScopeNode } from '../../shared/utils/scopeTypes';
import { RulesCatalogState } from './state';
import { RulesCatalogCommand } from './commands';

// =============================================================================
// SCOPE-TREE TRAVERSAL HELPERS
// =============================================================================

/**
 * Depth-first search for a node by id within the (sparse/lazy) scope tree.
 * Returns `null` when the tree is empty or the id is not present (e.g. a pinned
 * id whose node has not been materialized yet).
 */
const findScopeNode = (
  root: ScopeNode | null,
  scopeId: string
): ScopeNode | null => {
  if ( !root ) return null;
  if ( root.id === scopeId ) return root;

  for ( const child of root.children ) {
    const match = findScopeNode( child, scopeId );
    if ( match ) return match;
  }

  return null;
};

/**
 * A rule is "active" when it would actually execute under the selected scope:
 * its display origin is `own` or `inherited` AND it is enabled. Rules annotated
 * `overridden` or `bypassed` are suppressed by the engine and MUST NOT count as
 * active (SDD core invariant). `disabled` rules are not active either.
 *
 * Note: the resolver only ever emits `own`/`inherited` for enabled winners (a
 * disabled rule is annotated `disabled`), so the `enabled !== false` check is
 * belt-and-braces — it keeps the predicate honest even if the rule is read via
 * a different path.
 */
const isActiveResolvedRule = ( resolved: ResolvedRule ): boolean => {
  const { rule, origin } = resolved;
  if ( origin === 'overridden' || origin === 'bypassed' || origin === 'disabled' ) {
    return false;
  }
  return rule.enabled !== false;
};

/**
 * Lowercased haystack of a rule's searchable text (name + description).
 */
const ruleSearchHaystack = ( resolved: ResolvedRule ): string => {
  const { name, description } = resolved.rule;
  return `${name ?? ''} ${description ?? ''}`.toLowerCase();
};

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * Derived state selectors.
 *
 * Heavy lifting (inheritance resolution, filtering, search, counts) lives here
 * so `normalize` stays light and components share one source of truth.
 */
export const selectors = {
  /**
   * The selected `ScopeNode` (looked up in `data.scopeTree` by
   * `ui.selectedScopeId`), or `null` when the tree is not yet built or the id
   * is not present.
   */
  selectedScopeNode: ( state: RulesCatalogState ): ScopeNode | null => {
    return findScopeNode( state.data.scopeTree, state.ui.selectedScopeId );
  },

  /**
   * Inheritance-resolved rules for the selected scope — every candidate rule
   * annotated with its display origin (own / inherited / overridden / bypassed /
   * disabled). Bypass rows are never emitted as entries (the resolver feeds them
   * only into the suppression calc).
   *
   * Handling of the `'all'` / account scope: the scope tree builder (ticket #8)
   * materializes the account/all root carrying just the account ancestor step,
   * so `resolveRulesForScope` maps account-level rules to `own` directly and
   * walks no ancestors (it returns `[]` for nodes with no ancestors). When the
   * tree is not yet built (no node found) we fall back to mapping every
   * non-bypass rule to `own` so the catalog still shows all account rules.
   */
  resolvedRules: ( state: RulesCatalogState ): ResolvedRule[] => {
    const node = selectors.selectedScopeNode( state );

    if ( node ) {
      return resolveRulesForScope( state.data.rules, node );
    }

    // Fallback for the synthetic 'all' view before the tree is built: show every
    // rule as own, excluding bypass rows (never rendered as entries).
    return state.data.rules
      .filter(( rule ) => rule.kind !== 'bypass' )
      .map(( rule ) => ({
        rule,
        origin: rule.enabled === false ? 'disabled' : 'own',
        fromLabel: ''
      }) as ResolvedRule );
  },

  /**
   * `resolvedRules` filtered by the toolbar filter (`all` | `active` |
   * `inactive`) AND the free-text search (`ui.rulesSearch`, over rule name +
   * description).
   *
   * - `active`   → rule would execute under this scope (origin own/inherited
   *   AND enabled). EXCLUDES overridden + bypassed (suppressed) + disabled.
   * - `inactive` → the complement (overridden, bypassed, disabled, or any
   *   non-active rule).
   */
  filteredResolvedRules: ( state: RulesCatalogState ): ResolvedRule[] => {
    const { rulesFilter, rulesSearch } = state.ui;
    const query = rulesSearch.trim().toLowerCase();

    return selectors.resolvedRules( state ).filter(( resolved ) => {
      if ( rulesFilter === 'active' && !isActiveResolvedRule( resolved )) return false;
      if ( rulesFilter === 'inactive' && isActiveResolvedRule( resolved )) return false;

      if ( query && !ruleSearchHaystack( resolved ).includes( query )) return false;

      return true;
    });
  },

  /**
   * Counts for the selected scope. `active` is the "M activas" count and
   * EXCLUDES overridden + bypassed + disabled rules (the SDD core invariant):
   * only rules the engine would actually execute are counted.
   */
  scopeRuleCounts: (
    state: RulesCatalogState
  ): { total: number; active: number } => {
    const resolved = selectors.resolvedRules( state );

    let active = 0;
    for ( const entry of resolved ) {
      if ( isActiveResolvedRule( entry )) active += 1;
    }

    return { total: resolved.length, active };
  }
};

// =============================================================================
// NORMALIZE
// =============================================================================

/**
 * Normalize state for consistency. Kept intentionally light — the derived
 * inheritance-resolved views live in the exported selectors.
 *
 * No clamping is required here: `selectedScopeId` is a free-form id (an unknown
 * id simply resolves to `null` → the `'all'` fallback in `resolvedRules`), and
 * the rules table has no pagination. Returned unchanged.
 */
const normalize = ( state: RulesCatalogState ): RulesCatalogState => {
  return state;
};

// =============================================================================
// PERMISSIONS
// =============================================================================

/**
 * Permission control — source-based command access.
 *
 * Per the permission matrix (SDD):
 * - `user` drives all `ui/*` interactions (selection, filters, search, pin).
 *   This INCLUDES `ui/requestRuleToggle` — the rule enable/disable submission
 *   trigger (the user flips a row Switch). Per memory
 *   `feedback_cdse_submission_trigger_canapply`, a user click that triggers a
 *   backend submit must be allowlisted in canApply, otherwise the click silently
 *   no-ops and `ruleTogglePolicy` never fires.
 * - `api` (policies) drives all `data/*` and `loading/*` commands — these carry
 *   the rules query / scope-tree results and the loading flags. Allowing api on
 *   `data/*` + `loading/*` is required or the rules-query / scope-reference-data
 *   policies silently no-op. `api` is ALSO allowed `ui/clearPendingToggle` so
 *   `ruleTogglePolicy` can reset the toggle trigger after its `SetRule` + refetch
 *   round-trip (success or failure).
 * - `policy` / `application` get full access for internal coordination.
 * - `ai` and `url` are denied (no AI-driven catalog mutation; deep-linking is a
 *   deferred nice-to-have — SDD Q4).
 */
/**
 * Command operations — the backend client command each user-sourced mutating UI
 * command maps to. Capability is DERIVED from the named client method, so no
 * capability string is hand-authored here. `ui/requestRuleToggle` re-issues the
 * rule via `SetRule`; `ui/requestRuleDelete` triggers `RemoveRule`. Other `ui/*`
 * commands (selection, filters, search, pin) are not in the map → ungated.
 */
export const commandOperations: CommandOperationMap<RulesCatalogState, RulesCatalogCommand> = {
  'automation/rulesCatalog/ui/requestRuleToggle': 'SetRule',
  'automation/rulesCatalog/ui/requestRuleDelete': 'RemoveRule'
};

const canApply = ({
  state,
  command,
  meta
}: {
  state: RulesCatalogState;
  command: RulesCatalogCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: policies execute data/* and loading/* commands, plus the
  // pending-toggle reset (a ui/* command the toggle policy must dispatch).
  if ( source === 'api' ) {
    return (
      type.startsWith( 'automation/rulesCatalog/data/' ) ||
      type.startsWith( 'automation/rulesCatalog/loading/' ) ||
      type === 'automation/rulesCatalog/ui/clearPendingToggle' ||
      type === 'automation/rulesCatalog/ui/clearPendingDelete'
    );
  }

  // User source: all UI interactions, incl. the rule-toggle/delete submission
  // triggers — allowlisted per `feedback_cdse_submission_trigger_canapply` and
  // gated by the backend operation declared in commandOperations.
  if ( source === 'user' ) {
    if ( !type.startsWith( 'automation/rulesCatalog/ui/' )) return false;
    return gateCommandByOperation( commandOperations, { state, command, meta });
  }

  // Policy / application source: full access for internal coordination.
  if ( source === 'policy' || source === 'application' ) {
    return true;
  }

  // Unknown sources (incl. ai, url): deny.
  return false;
};

// =============================================================================
// VALIDATE
// =============================================================================

/** No form validation needed for the rules catalog view. */
const validate = ( _state: RulesCatalogState ): Record<string, string> | null => {
  return null;
};

export const invariants: Invariants<RulesCatalogState, RulesCatalogCommand> = {
  normalize,
  canApply,
  validate
};
